import Icon from '@ant-design/icons';
import { Button, Col, Divider, Row, Select, Table, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link } from 'react-router-dom';
import checklistDetailsReportApi from '../../../../api/ChecklistDetailsReportApi';
import hubApi from '../../../../api/HubApi';
import projectApi from '../../../../api/ProjectApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { Page } from '../../../../model/elements';
import { Hub, Project } from '../../../../model/entities';
import { ChecklistDetailsReport } from '../../../../model/entities-report';
import { ReactComponent as AddSvg } from '../../../../resources/images/add.svg';
import notificationService from '../../../../services/NotificationService';
import stringService from '../../../../services/StringService';
import tableService from '../../../../services/TableService';
import styles from './ChecklistDetailsReportsPage.module.scss';

class ChecklistDetailsReportsPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = { hubs: [], projects: [] };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'loading' });

            const checklistDetailsReportsPage = await checklistDetailsReportApi.list(
                0,
                tableService.pageSize,
                'audit.created',
                true,
            );
            const hubs = await hubApi.list(true);
            const hub = hubs && hubs.length > 0 ? hubs[0] : undefined;
            const projects = hub ? await projectApi.list(hub!.provider, hub!.id) : [];

            this.setState({ checklistDetailsReportsPage, hubs, hub, projects });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    list = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { searchText, project } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const page = pagination.current!;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            const checklistDetailsReportsPage = await checklistDetailsReportApi.list(
                page - 1,
                pageSize,
                sortField,
                sortOrder,
                project?.id,
                searchText,
            );
            this.setState({ checklistDetailsReportsPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    search = async (searchText: string) => {
        const { checklistDetailsReportsPage, project } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const pageSize = checklistDetailsReportsPage!.size;
            const sortField = checklistDetailsReportsPage!.sort.field!;
            const sortOrder = checklistDetailsReportsPage!.sort.order!;
            const checklistDetailsReportsPageNew = await checklistDetailsReportApi.list(
                0,
                pageSize,
                sortField,
                sortOrder,
                project?.id,
                searchText,
            );
            this.setState({ checklistDetailsReportsPage: checklistDetailsReportsPageNew, searchText });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    getProjectName = (projectId: string): string => {
        const { projects } = this.state;

        const project = projects.find((p) => p.id === projectId)!;
        return project.name;
    };

    /**
     * Selects a hub.
     * @param hubId - the hub id
     */
    selectHub = async (hubId?: string) => {
        const { hubs } = this.state;
        try {
            this.setState({ loading: 'loadingProjects' });

            const hub = hubs.find((p) => p.id === hubId);
            const projects = hub ? await projectApi.list(hub!.provider, hub!.id) : [];
            const project = undefined;

            this.setState({ hub, projects, project });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Selects a project.
     * @param projectId - the project id
     */
    selectProject = async (projectId: string) => {
        const { projects, searchText, checklistDetailsReportsPage } = this.state;

        try {
            this.setState({ loading: 'loading' });
            const pageSize = checklistDetailsReportsPage!.size;
            const sortField = checklistDetailsReportsPage!.sort.field!;
            const sortOrder = checklistDetailsReportsPage!.sort.order!;
            const project = projects.find((p) => p.id === projectId);
            const updatedChecklistDetailsReportsPage = await checklistDetailsReportApi.list(
                0,
                pageSize,
                sortField,
                sortOrder,
                projectId,
                searchText,
            );

            this.setState({ project, checklistDetailsReportsPage: updatedChecklistDetailsReportsPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSelector()}
                {this.renderToolbar()}
                {this.renderTable()}
            </>
        );
    };

    renderSelector = (): React.ReactElement | undefined => {
        const { hubs, hub, projects, project, loading } = this.state;

        const hubOptions = hubs.map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {`${hub.provider} > ${hub.name}`}
            </Select.Option>
        ));
        const projectOptions = projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));

        return (
            <div className={styles.selector}>
                <Row gutter={[48, 24]} align="bottom">
                    <Col span={7} className={styles.field}>
                        <FormattedMessage id="reports.hub" tagName="label" />
                        <Select
                            size="large"
                            className={styles.hubs}
                            value={hub?.id}
                            onChange={this.selectHub}
                            placeholder={<FormattedMessage id="reports.hub.placeholder" />}
                            loading={loading === 'loading'}
                        >
                            {hubOptions}
                        </Select>
                    </Col>
                    <Col span={10} className={styles.field}>
                        <FormattedMessage id="reports.project" tagName="label" />
                        <Select
                            size="large"
                            className={styles.projects}
                            value={project?.id}
                            onChange={this.selectProject}
                            placeholder={<FormattedMessage id="reports.project.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                            loading={loading === 'loadingProjects'}
                            allowClear
                        >
                            {projectOptions}
                        </Select>
                    </Col>
                </Row>

                <Divider />
            </div>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const placeholder: string = intl.formatMessage({ id: 'checklistDetailsReports.search' });

        return (
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={this.search} size="large" className="search" allowClear />
                <Link to="/reports/checklist-details/new">
                    <Tooltip title={<FormattedMessage id="checklistDetailsReports.new.tooltip" />}>
                        <Button type="primary" size="large" icon={<Icon component={AddSvg} />}>
                            <FormattedMessage id="checklistDetailsReports.new" tagName="span" />
                        </Button>
                    </Tooltip>
                </Link>
            </div>
        );
    };

    renderTable = (): React.ReactElement | undefined => {
        const { checklistDetailsReportsPage, loading } = this.state;
        const items = checklistDetailsReportsPage ? checklistDetailsReportsPage.content : [];
        const columns: ColumnsType<ChecklistDetailsReport> = [
            {
                title: <FormattedMessage id="checklistDetailsReport.checklistDetailsReport" />,
                dataIndex: 'name',
                key: 'name',
                sorter: true,
                render: (value: string, checklistDetailsReport: ChecklistDetailsReport) => (
                    <Link to={`/reports/checklist-details/${checklistDetailsReport.id}`}>{value}</Link>
                ),
            },
            {
                title: <FormattedMessage id="checklistDetailsReport.project" />,
                dataIndex: 'projectId',
                key: 'projectId',
                render: (value: string, checklistDetailsReport: ChecklistDetailsReport) => (
                    <Link to={`/reports/checklist-details/${checklistDetailsReport.id}`}>
                        {this.getProjectName(value)}
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="checklistDetailsReport.creator" />,
                dataIndex: 'audit.creator.firstName',
                key: 'creator',
                sorter: true,
                render: (value: string, checklistDetailsReport: ChecklistDetailsReport) => (
                    <Link to={`/reports/checklist-details/${checklistDetailsReport.id}`}>
                        {checklistDetailsReport.audit?.creator.firstName}{' '}
                        {checklistDetailsReport.audit?.creator.lastName}
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="checklistDetailsReport.created" />,
                dataIndex: 'audit.created',
                key: 'created',
                sorter: true,
                defaultSortOrder: 'descend',
                align: 'center',
                render: (value: string, checklistDetailsReport: ChecklistDetailsReport) => (
                    <Link to={`/reports/checklist-details/${checklistDetailsReport.id}`}>
                        {
                            <FormattedDate
                                value={checklistDetailsReport.audit?.created as any}
                                day="2-digit"
                                month="2-digit"
                                year="numeric"
                            />
                        }
                    </Link>
                ),
            },
        ];

        return (
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(checklistDetailsReportsPage)}
                rowKey="id"
                onChange={this.list}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="reports">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(ChecklistDetailsReportsPage);

interface Props extends WrappedComponentProps {}

interface State {
    checklistDetailsReportsPage?: Page<ChecklistDetailsReport>;
    loading?: 'loadingProjects' | 'loading';
    searchText?: string;
    hubs: Hub[];
    hub?: Hub;
    projects: Project[];
    project?: Project;
}
